import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { submitAccessRequest } from "../services/accessRequestService";

// Opzioni dei select (devono restare allineate alla whitelist server-side in functions/index.js)
const PROFESSIONI = [
  "IT/Sviluppo",
  "Design/Creativo",
  "Consulenza",
  "Legale",
  "Psicologia/Coaching",
  "Marketing",
  "Ingegneria",
  "Altro",
];

const ANNI_PIVA = ["Primo anno", "1-3 anni", "3-5 anni", "5+ anni"];

const FONTI = ["Social", "Passaparola", "Ricerca Google", "Forum/Community", "Altro"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    email: "",
    nome: "",
    professione: "",
    anniPiva: "",
    fonte: "",
    strumenti: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const update = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.nome.trim()) {
      setError("Inserisci il tuo nome.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Inserisci un'email valida.");
      return;
    }
    if (!form.professione) {
      setError("Seleziona la professione.");
      return;
    }
    if (!form.anniPiva) {
      setError("Seleziona gli anni di attività P.IVA.");
      return;
    }
    if (!form.fonte) {
      setError("Seleziona come ci hai conosciuto.");
      return;
    }

    setLoading(true);
    try {
      await submitAccessRequest({
        nome: form.nome.trim(),
        email: form.email.trim(),
        professione: form.professione,
        anniAttivita: form.anniPiva,
        fonte: form.fonte,
        strumentiUsati: form.strumenti.trim() || "Nessuno indicato",
      });
      setSuccess(true);
    } catch (err) {
      console.error("Errore invio richiesta accesso:", err);
      setError("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const labelClass = "block text-sm font-medium mb-1.5 text-neutral-700";
  const inputClass =
    "w-full px-3 py-2.5 border-2 border-neutral-300 rounded-lg text-sm bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-gradient-to-br from-forfy-50 via-white to-neutral-50">
      <div className="w-full max-w-lg rounded-2xl shadow-xl p-8 bg-white border border-neutral-100">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center justify-center gap-3 mb-4">
            <img src="/landing-assets/logo-forfy.svg" alt="Forfy" className="w-12 h-12" />
          </Link>

          {!success && (
            <>
              <h1 className="text-2xl font-display font-semibold text-neutral-900">
                Richiedi accesso
              </h1>
              <p className="mt-2 text-sm text-neutral-500">
                Forfy è in <strong>beta privata</strong>, i posti sono limitati.
                Lascia i tuoi dati: ti contatteremo appena si libera un posto.
              </p>
            </>
          )}
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-forfy-100 mb-4">
              <CheckCircle2 className="w-7 h-7 text-forfy-700" />
            </div>
            <h2 className="text-xl font-display font-semibold text-neutral-900">
              Richiesta inviata! Ti contatteremo presto.
            </h2>
            <p className="mt-2 text-sm text-neutral-500">
              Abbiamo ricevuto la tua richiesta. Appena si libera un posto nella
              beta ti scriviamo all'indirizzo che ci hai indicato.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium text-forfy-700 hover:text-forfy-800"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alla home
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Nome */}
            <div>
              <label htmlFor="nome" className={labelClass}>
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                id="nome"
                type="text"
                value={form.nome}
                onChange={update("nome")}
                placeholder="Mario Rossi"
                className={inputClass}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={labelClass}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                value={form.email}
                onChange={update("email")}
                placeholder="mario@esempio.it"
                className={inputClass}
                required
              />
            </div>

            {/* Professione */}
            <div>
              <label htmlFor="professione" className={labelClass}>
                Professione / settore <span className="text-red-500">*</span>
              </label>
              <select
                id="professione"
                value={form.professione}
                onChange={update("professione")}
                className={inputClass}
              >
                <option value="">Seleziona…</option>
                {PROFESSIONI.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Anni P.IVA */}
            <div>
              <label htmlFor="anniPiva" className={labelClass}>
                Anni di attività P.IVA <span className="text-red-500">*</span>
              </label>
              <select
                id="anniPiva"
                value={form.anniPiva}
                onChange={update("anniPiva")}
                className={inputClass}
              >
                <option value="">Seleziona…</option>
                {ANNI_PIVA.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            {/* Fonte */}
            <div>
              <label htmlFor="fonte" className={labelClass}>
                Come ci hai conosciuto? <span className="text-red-500">*</span>
              </label>
              <select
                id="fonte"
                value={form.fonte}
                onChange={update("fonte")}
                className={inputClass}
              >
                <option value="">Seleziona…</option>
                {FONTI.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            {/* Strumenti simili */}
            <div>
              <label htmlFor="strumenti" className={labelClass}>
                Hai già usato strumenti simili? Se sì, quali?
              </label>
              <textarea
                id="strumenti"
                value={form.strumenti}
                onChange={update("strumenti")}
                rows={3}
                placeholder="Opzionale"
                className={inputClass}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm bg-forfy-600 text-white hover:bg-forfy-700 disabled:opacity-60 transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Invio in corso…" : "Invia richiesta"}
            </button>

            <p className="text-center text-xs text-neutral-500">
              Hai già un account?{" "}
              <Link to="/accedi" className="font-medium text-forfy-700 hover:text-forfy-800">
                Accedi
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
